import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import Header from '../../../components/Header';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@emotion/react';
import { tokens } from '../../../theme';

const BookingDetails = () => {
  const { id } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const token = localStorage.getItem('token');

  const [booking, setBooking] = useState({
    contractNo: '',
    passengerId: '',
    destinationId: '',
    hotel: '',
    bus: '',
    startDestination: '',
    guideId: '',
    partner: '',
    notes: '',
    bookingStartDate: '',
    bookingEndDate: '',
  });

  const [countryNames, setCountryNames] = useState([]);
  const [guideNames, setGuideNames] = useState([]);
  const [passengerNames, setPassengerNames] = useState([]);
  const [passengerSearchInput, setPassengerSearchInput] = useState('');

  // Helper to ensure date is in YYYY-MM-DD format for input fields
  function toInputDate(dateString) {
    if (!dateString) return '';

    const date = new Date(dateString); // Local timezone is automatically applied

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${year}-${month}-${day}`;
  }

  // Helper to display date in DD-MM-YYYY format
  function displayDate(dateString) {
    if (!dateString) return '';
    if (dateString.includes('T')) dateString = dateString.split('T')[0];
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) return dateString;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return `${day}-${month}-${year}`;
    }
    return dateString;
  }

  const getBooking = async () => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/bookings/${id}`, { headers: { token } });
    const data = await res.json();
    setBooking({
      ...data.data,
      bookingStartDate: data.data.bookingStartDate ? toInputDate(data.data.bookingStartDate) : '',
      bookingEndDate: data.data.bookingEndDate ? toInputDate(data.data.bookingEndDate) : '',
    });
  };

  const getCountryNames = async () => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/countrynames`, { headers: { token } });
    const data = await res.json();
    setCountryNames(data);
  };

  const getGuideNames = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/all-names`, { headers: { token } });
      const data = await res.json();
      if (data.success) {
        setGuideNames(data.guides);
      } else {
        setGuideNames([]);
      }
    } catch (error) {
      setGuideNames([]);
    }
  };

  // With no search term, returns only the latest 20 passengers (cheap default instead of
  // loading the entire passenger table, which has thousands of rows).
  const getPassengerNames = async (search = '') => {
    const params = new URLSearchParams(search ? { search } : {});
    const res = await fetch(`${process.env.REACT_APP_APILINK}/passengernames?${params.toString()}`, {
      headers: { token },
    });
    const data = await res.json();
    if (data.success) {
      setPassengerNames(data.passengers);
    }
  };

  // Ensures the booking's currently-assigned passenger always appears as a selectable option,
  // even if they're not among the latest 20 default results (e.g. an older passenger).
  const ensurePassengerIncluded = async (passengerId) => {
    if (!passengerId) return;
    const params = new URLSearchParams({ id: passengerId });
    const res = await fetch(`${process.env.REACT_APP_APILINK}/passengernames?${params.toString()}`, {
      headers: { token },
    });
    const data = await res.json();
    if (data.success && data.passengers?.length) {
      setPassengerNames((prev) => {
        if (prev.some((p) => String(p.id) === String(passengerId))) return prev;
        return [...data.passengers, ...prev];
      });
    }
  };

  useEffect(() => {
    getBooking();
    getCountryNames();
    getGuideNames(); // Get all guides immediately
  }, []);

  // Debounce the passenger search so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      getPassengerNames(passengerSearchInput);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengerSearchInput]);

  // Once the booking loads with its assigned passenger, make sure that passenger is
  // guaranteed to be selectable even if they're not in the default latest-20 list.
  useEffect(() => {
    if (booking.passengerId) {
      ensurePassengerIncluded(booking.passengerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.passengerId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBooking({
      ...booking,
      [name]: value,
    });
  };

  const handleAutoCompleteChange = (name, value) => {
    setBooking({ ...booking, [name]: value });
  };

  const handlevalidation = () => {
    const {
      contractNo,
      passengerId,
      destinationId,
      hotel,
      bus,
      startDestination,
      guideId,
      partner,
      notes,
      bookingStartDate,
      bookingEndDate,
      paymentStatus,
    } = booking;
    if (
      !contractNo ||
      !passengerId ||
      !destinationId ||
      !hotel ||
      !bus ||
      !startDestination ||
      !guideId ||
      !partner ||
      !bookingStartDate ||
      !bookingEndDate
    ) {
      setOpenToast(true);
      setToastmessage('Please fill all Details');
      return false;
    }
    return true;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (handlevalidation()) {
      // Only send fields the v2 API expects — omit packageId/packageIds so linked packages are not cleared
      const bookingToSend = {
        contractNo: booking.contractNo,
        passengerId: booking.passengerId,
        destinationId: booking.destinationId,
        hotel: booking.hotel,
        bus: booking.bus,
        startDestination: booking.startDestination,
        guideId: booking.guideId,
        partner: booking.partner,
        notes: booking.notes ?? '',
        bookingStartDate: booking.bookingStartDate,
        bookingEndDate: booking.bookingEndDate,
      };
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/bookings/${id}`, {
        method: 'PUT',
        headers: {
          'Content-type': 'application/json',
          token,
        },
        body: JSON.stringify(bookingToSend),
      });
      const data = await res.json();
      if (data.message) {
        setOpenToast(true);
        setToastmessage(data.message);
        if (data.success) {
          setTimeout(() => {
            navigate('/booking');
          }, 2000);
        }
      }
    }
  };

  // Toast
  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" rowGap="10px" flexWrap="wrap-reverse">
        <Header title="BOOKING DETAILS" subtitle="" />

        <Box>
          <Link to={-1}>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: colors.blueAccent[800],
                },
              }}
            >
              Go Back
            </Button>
          </Link>
        </Box>
      </Box>

      <form onSubmit={handleFormSubmit}>
        <Box
          style={{
            display: 'grid',
            gap: '1.25rem',
            paddingBottom: '20px',
          }}
        >
          {/* Contract No */}
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Contract No"
            onChange={handleChange}
            value={booking.contractNo}
            name="contractNo"
            required
          />

          {/* Destination */}
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Destination"
            onChange={(e) => {
              setBooking({ ...booking, [e.target.name]: e.target.value, guideId: '' });
            }}
            value={booking.destinationId || ''}
            name="destinationId"
            select
            required
          >
            {countryNames &&
              countryNames.map((country) => (
                <MenuItem key={country.CountryID} value={country.CountryID}>
                  {country.CountryName}
                </MenuItem>
              ))}
          </TextField>

          {/* Guide */}
          {guideNames && (
            <Autocomplete
              options={guideNames}
              value={guideNames.find((guide) => guide.id === booking.guideId) || null}
              getOptionLabel={(option) => `${option.name} ${option.surname} (${option.email})`}
              onChange={(e, value) => {
                handleAutoCompleteChange('guideId', value ? value.id : null);
              }}
              renderInput={(params) => <TextField {...params} variant="filled" label="Guide" required />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          )}

          {/* Passenger — defaults to the latest 20 passengers; typing searches all passengers server-side */}
          {passengerNames && (
            <Autocomplete
              options={passengerNames}
              value={passengerNames.find((passenger) => String(passenger.id) === String(booking.passengerId)) || null}
              getOptionLabel={(option) => `${option.name} ${option.surname} (${option.email})`}
              onChange={(e, value) => {
                setBooking({ ...booking, passengerId: value ? value.id : null });
              }}
              onInputChange={(e, newInputValue, reason) => {
                if (reason === 'input') setPassengerSearchInput(newInputValue);
              }}
              filterOptions={(options) => options}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="filled"
                  label="Passenger"
                  required
                  helperText="Showing latest 20 passengers — type a name or email to search all passengers"
                />
              )}
              noOptionsText="No passengers found"
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            />
          )}

          {/* Multiple Passengers */}
          {/* {passengerNames && (
            <Autocomplete
              options={passengerNames}
              multiple
              value={passengerNames.filter((passenger) => booking.P_IDs.includes(passenger.P_ID))}
              getOptionLabel={(option) => `${option.P_Name} ${option.P_Surname} (${option.P_Email})`}
              onChange={(e, value) => {
                setBooking({ ...booking, P_IDs: value.map((passenger) => passenger.P_ID) });
              }}
              renderInput={(params) => <TextField {...params} variant="filled" label="Passengers" />}
            />
          )} */}

          {/* Hotel */}
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Hotel"
            onChange={handleChange}
            value={booking.hotel}
            name="hotel"
            required
          />

          {/* Bus */}
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Bus"
            onChange={handleChange}
            value={booking.bus}
            name="bus"
            required
          />

          {/* Start Destination */}
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Start Destination"
            onChange={handleChange}
            value={booking.startDestination}
            name="startDestination"
            required
          />

          {/* Partner */}
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Partner"
            onChange={handleChange}
            value={booking.partner}
            name="partner"
            required
          />

          {/* Notes */}
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Notes"
            onChange={handleChange}
            value={booking.notes}
            name="notes"
          />
          {/* Booking Date */}
          <Box display="flex" gap="10px" flexWrap={{ xs: 'wrap', sm: 'nowrap' }}>
            <TextField
              type="date"
              fullWidth
              variant="filled"
              label="Booking Start Date"
              onChange={handleChange}
              value={booking.bookingStartDate}
              name="bookingStartDate"
              InputLabelProps={{ shrink: true, style: { color: colors.grey[300] } }}
              style={{ colorScheme: 'dark' }}
              required
            />
            <TextField
              type="date"
              fullWidth
              variant="filled"
              label="Booking End Date"
              onChange={handleChange}
              value={booking.bookingEndDate}
              name="bookingEndDate"
              InputLabelProps={{ shrink: true, style: { color: colors.grey[300] } }}
              style={{ colorScheme: 'dark' }}
              required
            />
          </Box>

          {/* Notes */}

          <Button
            type="submit"
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              width: 'fit-content',
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 30px',
              '&:hover': {
                backgroundColor: colors.blueAccent[800],
              },
            }}
          >
            Submit
          </Button>
        </Box>
      </form>

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default BookingDetails;
